import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { IconComponent } from '../../atoms/icon/icon.component';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';

import { InfoTeaser } from '../../../shared/interfaces/info-teaser.interface';

import {
  CaasComponent,
  CaasIconValue,
} from '../../../shared/interfaces/caas/caas.interface';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-info-teasers',
  standalone: true,
  imports: [IconComponent, CommonModule, RouterModule, NgxSkeletonLoaderModule],
  templateUrl: './info-teasers.component.html',
  styleUrls: ['./info-teasers.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class InfoTeasersComponent implements OnChanges {
  @ViewChildren('text') text: QueryList<ElementRef> =
    new QueryList<ElementRef>();
  @Input() icon: string = 'envelope-open-text';
  @Input() infoTeasers: InfoTeaser[] = [];
  @Input() caasData?: CaasComponent;
  @Input() numbering: boolean = false;

  public readonly Sizes = Sizes;
  public readonly Colors = Colors;
  public readonly HrefType = HrefType;
  public isLoading = true;

  constructor(
    private readonly caasBodytextService: CaasBodytextService,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['caasData'] &&
      (this.caasData?.formData?.st_items?.value?.length ?? 0) > 0
    ) {
      safeAssign(
        () => {
          this.numbering = this.caasData?.formData?.st_number?.value ?? false;
        },
        'Assigning Numbering',
        this.errorHandler
      );
      this.setCaasInfoTeasers();
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
  }

  replaceTextWithLinks(teaserText: string, infoTeaser: InfoTeaser) {
    infoTeaser.links?.forEach((link) => {
      if (teaserText.includes(link.label)) {
        teaserText = teaserText.replace(
          link.label,
          `<a href="${link.href}" target="${link.target ?? '_self'}">${link.label}</a>`
        );
      }
    });
    return teaserText;
  }

  setCaasInfoTeasers() {
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_items?.value) {
          this.infoTeasers = this.caasData?.formData?.st_items?.value.map(
            (infoTeaser) => {
              let content = '';
              let icon: string | CaasIconValue = '';
              safeAssign(
                () => {
                  content = this.caasBodytextService.extractTextEditorContent(
                    infoTeaser?.formData?.st_bodytext?.value ?? ''
                  );
                },
                'Assigning Content',
                this.errorHandler
              );
              safeAssign(
                () => {
                  icon = infoTeaser?.formData?.st_icon?.value ?? '';
                },
                'Assigning Icon',
                this.errorHandler
              );

              return {
                icon: icon,
                text: content,
              };
            }
          );
        }
      },
      'Assigning Info Teasers',
      this.errorHandler
    );
  }
}
